import { getLogger } from '../logging';
import { toMessageBundle } from './MessageBundle';
import { getPseudoI18n, getPseudoL10n, isPseudoLocaleForI18n, isPseudoLocaleForL10n } from './pseudo';
const DEFAULT_LOCALE = 'en';
const DEFAULT_L10N_TAG = 'P95';
const LT_SEARCH_LOCALES = ['it', 'fr', 'es', 'de', 'pl', 'ru']; // Order is significant, do not change.
const logger = getLogger('I18nextMergeBackend');

/**
 * I18next backend module that accepts multiple other backend modules and merges the
 * bundles from each backend together.
 */
export class I18nextMergeBackend {
  type = 'backend';
  backends = [];
  init(services, options, i18nextOptions) {
    this.backends = options.backends.map(b => {
      const {
        instance: backend,
        options: backendOptions
      } = b;
      backend.init(services, backendOptions, i18nextOptions);
      return backend;
    });
  }
  async read(language, namespace, callback) {
    // eslint-disable-next-line @sas/i18n/jsx-to-localize
    logger.info(`Request for bundle '${namespace}' in ${language}`);
    let bundle;
    try {
      if (isPseudoLocaleForI18n(language)) {
        bundle = await this.readPseudoI18nLocale(language, namespace);
      } else if (isPseudoLocaleForL10n(language)) {
        bundle = await this.readPseudoL10nLocale(language, namespace);
      } else {
        bundle = await this.readLocale(language, namespace);
      }
      logger.info(`Result for bundle '${namespace}' in ${language}`);
      logger.info(JSON.stringify(bundle, undefined, 2));
      callback(null, bundle.messages);
    } catch (error) {
      callback(error, false);
    }
  }
  async readLocale(language, namespace) {
    const requests = this.backends.map(backend => {
      return {
        backend,
        language
      };
    });

    // Backends with defaultProvider set to true should be called a second time with the
    // default locale. This will fill in any missing messages from the default bundle.
    for (const backend of this.backends) {
      if (backend.defaultProvider) {
        requests.push({
          backend,
          language: DEFAULT_LOCALE
        });
      }
    }
    const requestPromises = requests.map(({
      backend,
      language
    }) => this.readBackendAsPromise(language, namespace, backend));
    const bundles = await Promise.all(requestPromises);
    // If the only returned results are from the default locale,
    // it may be that backends will return something valid for something
    // later in the fallback chain (ie [fr-FR, fr, en]).
    // In this case, we should respect the chain and return an error.
    const bundleFound = bundles.slice(0, this.backends.length).some(r => r !== null);
    if (!bundleFound) {
      throw new Error('No bundles for that locale');
    }
    return mergeBundles(bundles);
  }
  async readPseudoI18nLocale(language, namespace) {
    logger.debug(`${namespace}/${language} readPseudoI18n`);
    // Start async fetch of pseudo-translator
    getPseudoI18n();
    const pseudoLocaleResults = Promise.all(this.backends.map(backend => this.readBackendAsPromise(language, namespace, backend)));
    const defaultResults = Promise.all(this.backends.map(backend => this.readBackendAsPromise(DEFAULT_LOCALE, namespace, backend)));
    const pseudoBundle = mergeBundles(await pseudoLocaleResults);
    const defaultBundle = mergeBundles(await defaultResults);
    const {
      pseudoLocalizeBundle
    } = await getPseudoI18n();
    defaultBundle.messages = pseudoLocalizeBundle(language, defaultBundle.messages);
    return mergeBundles([pseudoBundle, defaultBundle]);
  }
  async readPseudoL10nLocale(language, namespace) {
    // Start async fetch of pseudo-translator
    getPseudoL10n();
    const pseudoLocaleResults = Promise.all(this.backends.map(backend => this.readBackendAsPromise(language, namespace, backend)));
    const regularLocale = language.substring(0, 2);
    const regularLocaleResults = Promise.all(this.backends.map(backend => this.readBackendAsPromise(regularLocale, namespace, backend)));
    const defaultResults = Promise.all(this.backends.map(backend => this.readBackendAsPromise(DEFAULT_LOCALE, namespace, backend)));
    const pseudoBundle = mergeBundles(await pseudoLocaleResults);
    const regularBundle = mergeBundles(await regularLocaleResults);
    const defaultBundle = mergeBundles(await defaultResults);
    let tag = DEFAULT_L10N_TAG;
    const pseudoL10n = await getPseudoL10n();
    if (language === 'lt-LT-pseudo') {
      const {
        getTag
      } = pseudoL10n;
      localeLoop: for (const searchLocale of LT_SEARCH_LOCALES) {
        for (const backend of this.backends) {
          const bundle = await this.readBackendAsPromise(searchLocale, namespace, backend);
          if (bundle) {
            const maybeTag = getTag(bundle);
            if (maybeTag) {
              tag = maybeTag;
              break localeLoop;
            }
          }
        }
      }
    }
    const pseudo4L10nBundle = pseudoL10n.pseudoLocalize4L10n(defaultBundle, regularBundle, language, tag);
    return mergeBundles([pseudoBundle, pseudo4L10nBundle]);
  }
  readBackendAsPromise(language, namespace, backend) {
    return new Promise(accept => {
      backend.read(language, namespace, (err, result) => {
        if (!err && result && typeof result === 'object') {
          accept(toMessageBundle(result));
        } else {
          accept(null);
        }
      });
    });
  }
  create(_languages, _namespace, _key, _fallbackValue) {}
}
function mergeBundles(bundles) {
  return bundles.filter(b => !!b).map(toMessageBundle).reduce((mergedBundle, bundle) => {
    return {
      ...mergedBundle,
      messages: {
        // Prefer to use content from the first bundle.
        ...bundle.messages,
        ...mergedBundle.messages
      },
      comments: mergeArraysWithNoDuplicates(mergedBundle.comments, bundle.comments)
    };
  }, {
    comments: [],
    messages: {}
  });
}
function mergeArraysWithNoDuplicates(arr1, arr2) {
  const strings = new Set([...arr1, ...arr2]);
  return Array.from(strings);
}
//# sourceMappingURL=I18nextMergeBackend.js.map